import { useCallback, useRef } from "react";
import {
  LiveFlashCommonField,
  LiveFlashKind,
  MemberActivityKind,
} from "@/types/frontend-types";
import type {
  FlagLiveFlash,
  PushMemberActivity,
  ReflectionNoteObservation,
  ReflectionsObservationInput,
} from "./session-live-signals.types";
import { makeMemberActivityExcerpt } from "./use-session-member-activity-signals";

interface UseSessionReflectionLiveObserverInput {
  flagLiveFlash: FlagLiveFlash;
  pushMemberActivity: PushMemberActivity;
}

interface SessionReflectionLiveObserver {
  markReflectionsObserved: (input: ReflectionsObservationInput) => void;
  resetReflectionObserver: () => void;
}

export function useSessionReflectionLiveObserver({
  flagLiveFlash,
  pushMemberActivity,
}: UseSessionReflectionLiveObserverInput): SessionReflectionLiveObserver {
  const previousReflectionIdsByVersionRef = useRef<Record<string, Set<string>>>(
    {},
  );

  const markReflectionsObserved = useCallback(
    (input: ReflectionsObservationInput): void => {
      const previousSet =
        previousReflectionIdsByVersionRef.current[input.versionId];
      if (!previousSet) {
        previousReflectionIdsByVersionRef.current[input.versionId] = new Set(
          input.reflectionNotes.map((note) => note.id),
        );
        return;
      }

      input.reflectionNotes.forEach((note) => {
        if (previousSet.has(note.id)) return;
        previousSet.add(note.id);
        flagReflectionArrival({ note, productId: input.productId, flagLiveFlash });
        pushReflectionMemberActivity({ note, pushMemberActivity });
      });
    },
    [flagLiveFlash, pushMemberActivity],
  );

  const resetReflectionObserver = useCallback((): void => {
    previousReflectionIdsByVersionRef.current = {};
  }, []);

  return { markReflectionsObserved, resetReflectionObserver };
}

function flagReflectionArrival(input: {
  note: ReflectionNoteObservation;
  productId: string;
  flagLiveFlash: FlagLiveFlash;
}): void {
  input.flagLiveFlash({
    kind: LiveFlashKind.Reflection,
    entityId: input.note.id,
    fields: [LiveFlashCommonField.Arrival],
    scopeProductId: input.productId,
  });
}

function pushReflectionMemberActivity(input: {
  note: ReflectionNoteObservation;
  pushMemberActivity: PushMemberActivity;
}): void {
  if (!input.note.authorMember) return;
  input.pushMemberActivity({
    memberId: input.note.authorMember,
    kind: MemberActivityKind.Reflection,
    excerpt: makeMemberActivityExcerpt({ text: input.note.note }),
  });
}
